// Collapse whitespace and truncate to roughly one sentence for list previews.
export function snippet(text: string | null | undefined, max = 90): string | null {
  if (!text) return null;
  const t = text.trim().replace(/\s+/g, " ");
  if (!t) return null;
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

interface LabelableAccount {
  id: number;
  name: string;
  institution: string | null;
}

/**
 * Disambiguates accounts that share a display name (e.g. several "Cash"
 * sub-accounts across brokers) by appending the institution. Unique names pass
 * through untouched.
 *
 * The currency cannot be the last resort here: an account does not have one.
 * It states balances, each naming its own asset, and a brokerage states
 * several currencies at once. Two accounts at one institution
 * sharing a name are disambiguated by their id, which is the only thing that
 * genuinely distinguishes them.
 */
export function accountLabel<T extends LabelableAccount>(account: T, all: readonly T[]): string {
  const sameName = all.filter((a) => a.name === account.name);
  if (sameName.length <= 1) return account.name;
  if (account.institution) {
    const sameInstitution = sameName.filter((a) => a.institution === account.institution);
    return sameInstitution.length <= 1
      ? `${account.name} (${account.institution})`
      : `${account.name} (#${account.id})`;
  }
  return `${account.name} (#${account.id})`;
}
